/**
 * Unified input manager.
 *
 * Abstracts keyboard, touch, and pointer input into game actions.
 * Supports input buffering, touch zones for mobile, and swipe detection.
 *
 * Mobile layout:
 * - Real mode: tap anywhere = jump
 * - Complex mode: touch position maps to player position (drag to move)
 */

export type GameAction = 'jump' | 'duck' | 'left' | 'right' | 'up' | 'down' | 'pause' | 'confirm';

interface BufferedAction {
  action: GameAction;
  time: number;
}

const INPUT_BUFFER_WINDOW = 0.1; // 100ms buffer for jump timing forgiveness

export class InputManager {
  /** Currently held actions */
  private held = new Set<GameAction>();

  /** Actions pressed this frame (edge-triggered) */
  private justPressed = new Set<GameAction>();

  /** Buffered actions for input forgiveness */
  private buffer: BufferedAction[] = [];

  /** Whether touch/pointer is currently down */
  pointerDown = false;

  /** Touch/pointer position (normalized 0-1) */
  pointerX = 0.5;
  pointerY = 0.5;

  /** Whether we're on a touch device */
  isTouchDevice = false;

  /** Current input mode affects how touch is interpreted */
  touchMode: 'jump' | 'directional' = 'jump';

  private boundHandlers: (() => void)[] = [];

  attach(canvas: HTMLCanvasElement): void {
    const onKeyDown = (e: KeyboardEvent) => {
      const action = this.keyToAction(e.code);
      if (action) {
        e.preventDefault();
        if (!this.held.has(action)) {
          this.justPressed.add(action);
          this.buffer.push({ action, time: performance.now() / 1000 });
        }
        this.held.add(action);
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      const action = this.keyToAction(e.code);
      if (action) {
        this.held.delete(action);
      }
    };

    const onPointerDown = (e: PointerEvent) => {
      e.preventDefault();
      this.pointerDown = true;
      this.updatePointerPos(e, canvas);

      if (e.pointerType === 'touch') this.isTouchDevice = true;

      if (this.touchMode === 'jump') {
        // Real mode: any tap = jump
        this.justPressed.add('jump');
        this.held.add('jump');
        this.buffer.push({ action: 'jump', time: performance.now() / 1000 });
      } else {
        // Complex mode: map touch position to directional input
        this.updateTouchDirections();
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      if (this.pointerDown) {
        this.updatePointerPos(e, canvas);
        if (this.touchMode === 'directional') {
          this.updateTouchDirections();
        }
      }
    };

    const onPointerUp = () => {
      this.pointerDown = false;
      this.held.delete('jump');
      // Clear directional holds from touch
      if (this.touchMode === 'directional') {
        this.held.delete('left');
        this.held.delete('right');
        this.held.delete('up');
        this.held.delete('down');
      }
    };

    const onTouchStart = (e: TouchEvent) => {
      e.preventDefault();
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('touchstart', onTouchStart, { passive: false });

    this.boundHandlers.push(
      () => window.removeEventListener('keydown', onKeyDown),
      () => window.removeEventListener('keyup', onKeyUp),
      () => canvas.removeEventListener('pointerdown', onPointerDown),
      () => canvas.removeEventListener('pointermove', onPointerMove),
      () => window.removeEventListener('pointerup', onPointerUp),
      () => canvas.removeEventListener('touchstart', onTouchStart),
    );
  }

  detach(): void {
    for (const cleanup of this.boundHandlers) cleanup();
    this.boundHandlers = [];
  }

  /** Call at the start of each update tick to process input state */
  update(): void {
    this.justPressed.clear();
    const now = performance.now() / 1000;
    this.buffer = this.buffer.filter((b) => now - b.time < INPUT_BUFFER_WINDOW);
  }

  isHeld(action: GameAction): boolean {
    return this.held.has(action);
  }

  isJustPressed(action: GameAction): boolean {
    return this.justPressed.has(action);
  }

  consumeBuffer(action: GameAction): boolean {
    const idx = this.buffer.findIndex((b) => b.action === action);
    if (idx !== -1) {
      this.buffer.splice(idx, 1);
      return true;
    }
    return false;
  }

  /** Map touch position to directional input for Complex mode */
  private updateTouchDirections(): void {
    // Clear previous directions
    this.held.delete('left');
    this.held.delete('right');
    this.held.delete('up');
    this.held.delete('down');

    // Map pointer position to directions
    // Center = (0.5, 0.5), deadzone = 0.1
    const dx = this.pointerX - 0.5;
    const dy = this.pointerY - 0.5;
    const deadzone = 0.08;

    if (dx < -deadzone) this.held.add('left');
    if (dx > deadzone) this.held.add('right');
    if (dy < -deadzone) this.held.add('up');    // Screen Y is inverted
    if (dy > deadzone) this.held.add('down');
  }

  private keyToAction(code: string): GameAction | null {
    switch (code) {
      case 'Space':
      case 'ArrowUp':
      case 'KeyW':
        return 'jump';
      case 'ArrowDown':
      case 'KeyS':
        return 'duck';
      case 'ArrowLeft':
      case 'KeyA':
        return 'left';
      case 'ArrowRight':
      case 'KeyD':
        return 'right';
      case 'Escape':
      case 'KeyP':
        return 'pause';
      case 'Enter':
        return 'confirm';
      default:
        return null;
    }
  }

  private updatePointerPos(e: PointerEvent, canvas: HTMLCanvasElement): void {
    const rect = canvas.getBoundingClientRect();
    this.pointerX = (e.clientX - rect.left) / rect.width;
    this.pointerY = (e.clientY - rect.top) / rect.height;
  }
}
